import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { findMissingRequired } from '../../helpers/warnRequired';
import {
  addApplicant,
  addJob,
  addClient,
} from '../../features/bookingForm/bookingFormSlice';
import Button from '../Button/Button';
import Input from '../Input/Input';
import Select from '../Select/Select';
import vectorDown from '../../assets/images/vectorDown.png';
import vectorUp from '../../assets/images/vectorUp.png';
import warning from '../../assets/images/warning.png';
import css from './ApplicantSection.module.css';
import 'react-datepicker/dist/react-datepicker.css';

const ApplicantSection = ({ onValidationChange }) => {
  const [isApplicant, setIsApplicant] = useState(false);
  const applicant = useSelector(state => state.bookingForm.formData.applicant);
  const job = useSelector(state => state.bookingForm.formData.job);
  const client = useSelector(state => state.bookingForm.formData.client);
  const { applicant_statuses, pay_types } = useSelector(
    state => state.referenceBook.bookData,
  );
  const dispatch = useDispatch();
  const [missing, setMissing] = useState([]);
  const sectionRef = useRef(null);

  const handleSectionShowAndHide = () => setIsApplicant(!isApplicant);

  const handleChange = e => {
    if (e.target.name === 'pay_type') {
      if (+e.target.value === 4) {
        dispatch(addClient({ ...client, outside_ir35: true }));
        dispatch(
          addJob({
            ...job,
            pay_type_external_id: pay_types.find(
              type => +type.id === +e.target.value,
            ).external_id,
          }),
        );
      } else if (+e.target.value === 6) {
        dispatch(
          addJob({
            ...job,
            pay_type_external_id: 440,
          }),
        );
      } else
        dispatch(
          addJob({
            ...job,
            pay_type_external_id: pay_types.find(
              type => +type.id === +e.target.value,
            ).external_id,
          }),
        );
    }

    // SECURITY: Sync applicant attributes to job section when updated
    // When user updates applicant position, specialty, or grade, update job section accordingly
    if (e.target.name === 'position_id' || e.target.name === 'specialty_id' || e.target.name === 'grade_id') {
      const updatedApplicant = { ...applicant, [e.target.name]: e.target.value };
      
      // Find matching option in job's available options (or use applicant's options if job's are empty)
      // SECURITY: Ensure job has positions/specialties/grades arrays populated from applicant
      let jobUpdate = { 
        ...job,
        // Ensure job has arrays populated from applicant if they're missing
        positions: job?.positions?.length > 0 ? job.positions : applicant?.positions || [],
        specialties: job?.specialties?.length > 0 ? job.specialties : applicant?.specialties || [],
        grades: job?.grades?.length > 0 ? job.grades : applicant?.grades || [],
      };
      
      if (e.target.name === 'position_id') {
        // SECURITY: Update job position_id even if it's not in job's positions array
        // The job may have more positions (from previous jobs) than the applicant's current RCRM profile
        // We should still sync the position_id, and the job's positions array should include all options
        const applicantPosition = applicant?.positions?.find(p => +p.id === +e.target.value);
        if (applicantPosition) {
          // Update position_id directly - don't require it to be in job's positions array
          jobUpdate.position_id = e.target.value;
          
          // Try to find position name from job's positions array, or use applicant's position name
          const jobPosition = jobUpdate.positions?.find(p => +p.id === +e.target.value);
          const positionName = jobPosition?.name || applicantPosition.name;
          
          // Update job title
          jobUpdate.title = `${positionName}: ${
            jobUpdate.specialties?.find(s => +s.id === +jobUpdate.specialty_id)?.name || ''
          } (${
            jobUpdate.grades?.find(g => +g.id === +jobUpdate.grade_id)?.name || ''
          })`;
        }
      } else if (e.target.name === 'specialty_id') {
        // Find specialty by ID in job's specialties list (or applicant's if job's is empty)
        const specialtiesList = jobUpdate.specialties?.length > 0 ? jobUpdate.specialties : applicant?.specialties || [];
        const matchingSpecialty = specialtiesList.find(s => +s.id === +e.target.value);
        if (matchingSpecialty) {
          jobUpdate.specialty_id = e.target.value;
          // Update job title
          jobUpdate.title = `${
            jobUpdate.positions?.find(p => +p.id === +jobUpdate.position_id)?.name || ''
          }: ${matchingSpecialty.name} (${
            jobUpdate.grades?.find(g => +g.id === +jobUpdate.grade_id)?.name || ''
          })`;
        }
      } else if (e.target.name === 'grade_id') {
        // SECURITY: For grades, find similar grade by name (not necessarily same ID)
        // This matches the backend logic where grades can have different IDs but same name
        const applicantGrade = applicant?.grades?.find(g => +g.id === +e.target.value);
        if (applicantGrade) {
          // Try to find grade in job's grades by name (case-insensitive)
          const gradesList = jobUpdate.grades?.length > 0 ? jobUpdate.grades : applicant?.grades || [];
          const matchingGrade = gradesList.find(g => 
            g.name && applicantGrade.name && 
            g.name.toLowerCase() === applicantGrade.name.toLowerCase()
          );
          
          if (matchingGrade) {
            jobUpdate.grade_id = matchingGrade.id;
            // Update job title
            jobUpdate.title = `${
              jobUpdate.positions?.find(p => +p.id === +jobUpdate.position_id)?.name || ''
            }: ${
              jobUpdate.specialties?.find(s => +s.id === +jobUpdate.specialty_id)?.name || ''
            } (${matchingGrade.name})`;
          }
        }
      }
      
      dispatch(addJob(jobUpdate));
    }

    dispatch(addApplicant({ ...applicant, [e.target.name]: e.target.value }));
  };

  useEffect(() => {
    const currentMissing = findMissingRequired(sectionRef);
    setMissing(currentMissing);
    if (onValidationChange) {
      onValidationChange(currentMissing.length > 0);
    }
  }, [applicant]);

  const hasErrors = missing.length > 0;

  return (
    <section>
      <Button
        type="sectionBtn"
        style={{
          ...( !isApplicant ? { background: 'rgba(13, 85, 130, 0.2)', color: 'var(--secondary)' } : {} ),
          ...( hasErrors ? { background: 'rgba(229,57,53,.10)', color: '#b71c1c', boxShadow: '0 0 0 2px rgba(229,57,53,.15) inset' } : {} ),
        }}
        onClick={handleSectionShowAndHide}
        title="Applicant section"
      >
        <img
          className={css.arrow}
          alt="arrow"
          src={isApplicant ? vectorUp : vectorDown}
        />
      </Button>
      <div
        ref={sectionRef}
        className={css.section}
        style={isApplicant ? { display: 'flex' } : { display: 'none' }}
      >
        <Input
          name="title"
          value={applicant.title || ''}
          size="x33"
          type="text"
          placeholder="Title"
          onChange={handleChange}
          readOnly
          required
        />
        <Input
          name="first_name"
          value={applicant.first_name || ''}
          size="x33"
          type="text"
          placeholder="Applicant First Name"
          onChange={handleChange}
          readOnly
          required
        />
        <Input
          name="last_name"
          value={applicant.last_name || ''}
          size="x33"
          type="text"
          placeholder="Applicant Last Name"
          onChange={handleChange}
          readOnly
          required
        />
        <Select
          name="status_id"
          value={applicant.status_id || ''}
          options={applicant_statuses}
          size="x25"
          defaultValue="Applicant Status"
          onChange={handleChange}
          required
        />
        <Select
          name="position_id"
          value={applicant.position_id || ''}
          options={applicant?.positions ?? []}
          size="x25"
          defaultValue={(applicant?.positions?.length ?? 0) > 0 ? 'Position' : ''} 
          onChange={handleChange}
          required
        />
        <Select
          name="specialty_id"
          value={applicant.specialty_id || ''}
          options={applicant.specialties}
          size="x25"
          defaultValue="Specialty"
          onChange={handleChange}
          required
        />
        <Select
          name="grade_id"
          value={applicant.grade_id || ''}
          options={applicant.grades}
          size="x25"
          defaultValue="Grade/band"
          onChange={handleChange}
          required
        />
        <Select
          name="pay_type"
          value={applicant.pay_type || ''}
          options={pay_types}
          size="x33"
          defaultValue="Payment Type"
          onChange={handleChange}
          required
        />
        <Input
          name="ltd_name"
          value={applicant.ltd_name || ''}
          size="x33"
          type="text"
          placeholder="LtD Name"
          onChange={handleChange}
          readOnly
        />
        <Input
          name="ni_number"
          value={applicant.ni_number || ''}
          size="x33"
          type="text"
          placeholder="NI Number"
          onChange={handleChange}
          readOnly
        />
        <Input
          name="gender"
          value={applicant.gender || ''}
          size="x33"
          type="text"
          placeholder="Gender"
          onChange={handleChange}
          readOnly
        />
        <Input
          name="nationality"
          value={applicant.nationality || ''}
          size="x33"
          type="text"
          placeholder="Nationality"
          onChange={handleChange}
          readOnly
          required
        />
        <Input
          name="flag_text"
          value={applicant.flag_text || ''}
          size="x33"
          type="text"
          placeholder="Flag Text"
          onChange={handleChange}
          readOnly
        />
        <Input
          name="file_sign_off_status"
          value={applicant.file_sign_off_status || ''}
          size={applicant.warning ? 'x33' : 'x50'}
          type="text"
          placeholder="Compliance Status"
          onChange={handleChange}
          readOnly
          required
        />
        <Input
          name="expiry_date"
          value={applicant.expiry_date || ''}
          size={applicant.warning ? 'x33' : 'x50'}
          type="text"
          placeholder="Expiry Date"
          onChange={handleChange}
          readOnly
        />
        {applicant.warning && (
          <div className={css.warnMessage}>
            <img className={css.warnImg} alt="warning" src={warning} />
            <p className={css.warnText}>{applicant.warning}</p>
          </div>
        )}
        <Input
          name="registration_number"
          value={applicant.registration_number || ''}
          size="x50"
          type="text"
          placeholder="Registration Number"
          onChange={handleChange}
          readOnly
          required
        />
        <Input
          name="registration_notes"
          value={applicant.registration_notes || ''}
          size="x50"
          type="text"
          placeholder="Registration Notes"
          onChange={handleChange}
          readOnly
        />
        <Input
          name="dbs_number"
          value={applicant.dbs_number || ''}
          size="x50"
          type="text"
          placeholder="DBS Number"
          onChange={handleChange}
          readOnly
        />
        <Input
          name="dbs_notes"
          value={applicant.dbs_notes || ''}
          size="x50"
          type="text"
          placeholder="DBS Notes"
          onChange={handleChange}
          readOnly
        />
      </div>
      <hr
        className={css.hr}
        style={isApplicant ? { marginBottom: '60px' } : null}
      ></hr>
    </section>
  );
};

export default ApplicantSection;
